<template>
  <div>
    <div class="row q-col-gutter-md q-mb-lg">
      <q-toggle
        v-model="section.config.siteSelectorHasTitle"
        class="col-2"
        label="Show Title"
        bottom-slots
      />
      <model-form-input
        v-if="section.config.siteSelectorHasTitle"
        field="config.siteSelectorTitle"
        value-type="String"
        class="col"
        label="Title"
      />
      <color-selection
        v-if="section.config.siteSelectorHasTitle"
        class="col-6"
        :color-name="section.config.siteSelectorTitleColor"
        :custom-color-hex="section.config.siteSelectorTitleColorHex"
        label="Title Color"
        :disable="section.isTranslation"
        @update:model-value="setTitleColor"
      />
    </div>

    <div class="row q-col-gutter-md q-mb-lg">
      <model-form-toggle
        field="config.siteSelectorHasSubtitle"
        value-type="Boolean"
        class="col-2"
        label="Show Subtitle"
      />
      <model-form-input
        v-if="section.config.siteSelectorHasSubtitle"
        field="config.siteSelectorSubtitle"
        value-type="String"
        class="col"
        label="Subtitle"
      />
      <model-form-checkbox
        v-if="section.config.siteSelectorHasSubtitle"
        field="config.siteSelectorHasLightText"
        value-type="Boolean"
        class="col-2"
        label="Light Text"
      />
    </div>

    <div class="row q-col-gutter-md q-mb-lg">
      <model-form-toggle
        field="config.siteSelectorHasImage"
        value-type="Boolean"
        class="col-2"
        label="Show Image"
      />
      <div
        v-if="section.config.siteSelectorHasImage"
        class="col-2 relative-position"
      >
        <image-uploader
          v-model="section.config.siteSelectorImage"
          class="col"
          min-height="50px"
          :image-url="section.siteSelectorImageUrl"
          :model="section"
        />
        <q-icon
          name="info"
          color="dark"
          size="32px"
          class="absolute-top-right"
          style="transform: translateX(100%);"
        >
          <q-tooltip>
            Recommended size 300px x 300px
          </q-tooltip>
        </q-icon>
      </div>
    </div>
    <div class="row q-col-gutter-sm q-mb-lg">
      <model-form-select
        field="config.siteSelectorWidgetDisplayType"
        value-type="String"
        class="col-6 col-sm-4"
        :options="widgetDisplayTypesOptions"
        map-options
        emit-value
        clearable
        label="Widget Display Type"
      />
      <model-form-input
        v-if="section.config.siteSelectorWidgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP"
        field="config.siteSelectorDisplayDelayInSeconds"
        value-type="Number"
        class="col-4 col-sm-4"
        label="Display Delay (seconds)"
        type="number"
        min="0"
        max="60"
        step="0.5"
      >
        <q-tooltip>Site Selector widget popup delay in second after the page load</q-tooltip>
      </model-form-input>
      <model-form-toggle
        v-if="section.config.siteSelectorWidgetDisplayType === WIDGET_DISPLAY_TYPES.POPUP"
        field="config.siteSelectorAllowDismiss"
        value-type="Boolean"
        class="col-6 col-sm-2"
        label="Allow Dismiss"
      />
      <model-form-checkbox
        field="config.siteSelectorRememberUserPreference"
        value-type="Boolean"
        class="col-8 col-sm-4"
        map-options
        emit-value
        label="Remember User Preference"
      />
    </div>
    <div class="row q-mb-sm q-mt-lg">
      <div class="text-bold">
        Site Selector Display
      </div>
    </div>
    <div
      class="row q-col-gutter-md q-mb-md items-start"
    >
      <model-form-select
        field="config.siteSelectorsDisplayType"
        value-type="String"
        class="col-6 col-sm-3"
        :options="siteSelectorsDisplayOptions"
        map-options
        emit-value
        label="Sites Display Type"
      />
      <template v-if="section.config.siteSelectorsDisplayType !== SITE_SELECTOR_DISPLAY_TYPES.BLOCK">
        <model-form-select
          field="config.siteSelectorButtonType"
          value-type="String"
          class="col-6 col-sm-3"
          :options="buttonTypeOptions"
          map-options
          emit-value
          clearable
          label="Button Type"
        />
        <color-selection
          class="col-6"
          :color-name="section.config.siteSelectorButtonColor"
          :custom-color-hex="section.config.siteSelectorButtonColorHex"
          label="Button Color"
          :disable="section.isTranslation"
          @update:model-value="setButtonColor"
        />
      </template>
      <template v-else>
        <color-selection
          class="col-6"
          :color-name="section.config.siteSelectorBlockBackgroundColor"
          :custom-color-hex="section.config.siteSelectorBlockBackgroundColorHex"
          label="Block Background Color"
          :disable="section.isTranslation"
          @update:model-value="setBackgroundColor"
        />
        <color-selection
          class="col-6"
          :color-name="section.config.siteSelectorBlockTextColor"
          :custom-color-hex="section.config.siteSelectorBlockTextColorHex"
          label="Block Text Color"
          :disable="section.isTranslation"
          @update:model-value="setTextColor"
        />
        <div
          class="col-6 col-sm-3"
        >
          <model-form-checkbox
            field="config.siteSelectorBlockHasTextCentred"
            value-type="Boolean"
            class="col-3"
            label="Centered"
          />
        </div>
        <div
          class="col-6 col-sm-3"
        >
          <q-item-label>Shadow</q-item-label>
          <q-slider
            v-model="section.config.siteSelectorBlockShadow"
            label
            label-always
            switch-label-side
            type="number"
            :min="0"
            :max="10"
            :step="1"
          />
        </div>
      </template>
      <div
        v-if="(section.config.siteSelectorsDisplayType !== SITE_SELECTOR_DISPLAY_TYPES.BLOCK && section.config.siteSelectorButtonType !== 'flat') || section.config.siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BLOCK"
        class="col-6 col-sm-3"
      >
        <q-item-label>Border Radius</q-item-label>
        <q-slider
          v-model="section.config.siteSelectorBorderRadius"
          label
          label-always
          switch-label-side
          type="number"
          :min="0"
          :max="30"
          :step="1"
        />
      </div>
    </div>

    <div class="row q-gutter-md q-mb-xl">
      <q-table
        v-model:pagination="pagination"
        class="col"
        :columns="[
          { name: 'name', align: 'left', label: 'Site Name', field: siteSelectorItem => siteSelectorItem[localizeKey('name')] || siteSelectorItem.name, sortable: true },
          { name: 'address', align: 'left', label: 'Site Address', field: 'address', sortable: true },
          { name: section.config.siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BLOCK ? 'image' : undefined, align: 'left', label: section.config.siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BLOCK ? 'Site Image' : undefined, component: 'img', field: section.config.siteSelectorsDisplayType === SITE_SELECTOR_DISPLAY_TYPES.BLOCK ? siteSelectorItem => (siteSelectorItemImageUrl(siteSelectorItem[localizeKey('image')]) || siteSelectorItemImageUrl(siteSelectorItem.image)) : undefined, sortable: true },
          { name: 'useExternalUrl', field: siteSelectorItem => !!siteSelectorItem.externalUrl, align: 'left', label: 'External Site', component: 'checkbox', sortable: true },
          { name: 'actions', label: 'Actions' }

        ]"
        :rows="section.config.siteSelectorItems"
        :rows-per-page-options="[0]"
        hide-pagination
        bordered
        flat
        wrap-cells
        row-key="key"
      >
        <template #top>
          <div class="q-table__title">
            Sites
          </div>

          <q-space />

          <q-btn
            class="q-mx-sm"
            label="Set Order"
            @click="$refs.orderDialog.show()"
          />
          <q-btn
            label="Add Site"
            outline
            @click="editSite(null)"
          />
        </template>
        <template #body-cell="props">
          <q-td
            :props="props"
          >
            <template v-if="props.col.component === 'checkbox'">
              <q-checkbox
                disable
                :model-value="props.value"
              />
            </template>

            <template v-else-if="props.col.name === 'description'||props.col.name === 'title'">
              <div :style="'max-width: 200px; white-space: normal;'">
                {{ props.value }}
              </div>
            </template>
            <template v-else-if="props.col.name === 'linkUrl'">
              <div :style="'max-width: 200px; white-space: normal; word-break: break-all'">
                {{ props.value }}
              </div>
            </template>
            <template v-else-if="props.col.component === 'img'">
              <img
                v-if="props.value"
                height="60px"
                :src="props.value"
              >
            </template>
            <template v-else>
              {{ props.value }}
            </template>
          </q-td>
        </template>
        <template
          #body-cell-actions="props"
        >
          <q-td
            :props="props"
          >
            <slot
              name="row-actions"
              v-bind="props"
            >
              <div class="row q-gutter-sm justify-end no-wrap">
                <slot
                  name="prepend-row-actions"
                  v-bind="props"
                />
                <q-btn
                  :hover="false"
                  label="Edit"
                  @click="editSite(props.row)"
                />
                <q-btn
                  :hover="false"
                  icon="more_vert"
                  round
                  @click.stop
                >
                  <q-menu>
                    <q-list style="min-width: 180px">
                      <q-item
                        v-close-popup
                        clickable
                        @click="editSite(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="edit" />
                        </q-item-section>
                        <q-item-section>
                          Edit
                        </q-item-section>
                      </q-item>
                      <q-separator />
                      <q-item
                        v-close-popup
                        clickable
                        @click="deleteSite(props.row)"
                      >
                        <q-item-section avatar>
                          <q-icon name="delete" />
                        </q-item-section>
                        <q-item-section>
                          {{ $t('actions.remove') }}
                        </q-item-section>
                      </q-item>
                    </q-list>
                  </q-menu>
                </q-btn>
              </div>
            </slot>
          </q-td>
        </template>
      </q-table>
    </div>
    <preview
      :section="section"
      class="q-mb-xl"
    />

    <set-display-order-dialog
      ref="orderDialog"
      :list="section.config.siteSelectorItems"
      display-field="name"
      @reorder="handleReorder"
    />
    <site-selector-form
      ref="siteSelectorForm"
      :section="section"
      :site-selectors-display-type="section.config.siteSelectorsDisplayType"
      @save="siteSelector => saveSite(siteSelector)"
    />
  </div>
</template>

<script>
import { LocalizedJsonObject } from 'src/models/model'
import Section, { BUTTON_TYPES, POPUP_FREQUENCY_TYPES, POPUP_TRIGGER_TYPES, SITE_SELECTOR_DISPLAY_TYPES, TEXT_FIELD_TYPES, WIDGET_DISPLAY_TYPES } from 'src/models/page/Section'
import { siteSelectorItemImageUrl } from 'src/models/page/SiteSelectorItem'
import ColorSelection from 'src/modules/admin/components/ColorSelection'
import ImageUploader from 'src/modules/admin/components/ImageUploader'
import SetDisplayOrderDialog from 'src/modules/admin/components/SetDisplayOrderDialog'
import SiteSelectorForm from 'src/modules/admin/section/SiteSelectorForm.vue'
import Preview from 'src/modules/consumer/components/Preview'

export default {
  name: 'SiteSelectorEditor',
  components: {
    ImageUploader,
    SetDisplayOrderDialog,
    SiteSelectorForm,
    Preview,
    ColorSelection
  },
  props: {
    section: {
      type: Section,
      required: true
    }
  },
  configuration: ['currentCircuit'],
  data () {
    return {
      addNewOptionError: null,
      pagination: {
        page: 1,
        rowsPerPage: 0
      }
    }
  },
  created () {
    _.extend(this, {
      siteSelectorItemImageUrl,
      buttonTypeOptions: this.$mh.asOptions(BUTTON_TYPES),
      textFieldTypeOptions: this.$mh.asOptions(TEXT_FIELD_TYPES),
      popupFrequencyOptions: this.$mh.asOptions(POPUP_FREQUENCY_TYPES),
      siteSelectorsDisplayOptions: this.$mh.asOptions(SITE_SELECTOR_DISPLAY_TYPES),
      widgetDisplayTypesOptions: this.$mh.asOptions(WIDGET_DISPLAY_TYPES),
      WIDGET_DISPLAY_TYPES,
      SITE_SELECTOR_DISPLAY_TYPES,
      BUTTON_TYPES,
      TEXT_FIELD_TYPES,
      POPUP_TRIGGER_TYPES,
      POPUP_FREQUENCY_TYPES
    })
  },
  methods: {
    saveSite (siteSelectorItem) {
      const index = _.findIndex(this.section.config.siteSelectorItems, { key: siteSelectorItem.key })
      if (index === -1) {
        this.section.config.siteSelectorItems.push(siteSelectorItem)
      } else {
        this.section.config.siteSelectorItems[index] = siteSelectorItem
      }
    },
    editSite (siteSelectorItem = null) {
      const index = siteSelectorItem ? _.findIndex(this.section.config.siteSelectorItems, { key: siteSelectorItem.key }) : null
      this.$refs.siteSelectorForm.show(index)
    },
    deleteSite (siteSelectorItem) {
      const index = _.findIndex(this.section.config.siteSelectorItems, { key: siteSelectorItem.key })
      this.section.config.siteSelectorItems.splice(index, 1)
    },
    handleReorder ({ reorderedList }) {
      this.section.config.siteSelectorItems = reorderedList
    },
    localizeKey (key) {
      const localizedKey = LocalizedJsonObject.localizeKey(key, this.section.language)[0]
      return localizedKey
    },
    setTitleColor (colorName, customColorHex) {
      this.section.config.siteSelectorTitleColor = colorName
      this.section.config.siteSelectorTitleColorHex = customColorHex
    },
    setButtonColor (colorName, customColorHex) {
      this.section.config.siteSelectorButtonColor = colorName
      this.section.config.siteSelectorButtonColorHex = customColorHex
    },
    setBackgroundColor (colorName, customColorHex) {
      this.section.config.siteSelectorBlockBackgroundColor = colorName
      this.section.config.siteSelectorBlockBackgroundColorHex = customColorHex
    },
    setTextColor (colorName, customColorHex) {
      this.section.config.siteSelectorBlockTextColor = colorName
      this.section.config.siteSelectorBlockTextColorHex = customColorHex
    }
  }
}

</script>

<style scoped>
</style>
